import React, { useEffect, useMemo, useState } from 'react'
import styles from './combined-rating-filter-mobile.module.scss'
import { GameZoneBounds, RatingFilter } from 'types/global'
import FilterMenuSection from './FilterMenuSection'

import FilterMenuPage from './FilterMenuPage'
import Separator from '../Separator'
import { useCombinedRatingFilter } from 'components/search/CombinedRatingFilter/hooks'
import {
  transformGameZoneValues,
  transformRatingForLabel,
} from 'components/search/CombinedRatingFilter/utils'
import CombinedRatingFilter from 'components/search/CombinedRatingFilter/CombinedRatingFilter'
import { useTranslation } from 'react-i18next'
import { useGameZone } from 'hooks/use-gamezone'

interface Props {
  id: string
  minRange: number
  maxRange: number
  stepSize: number
  rating: RatingFilter | null
  title: string
  setRating: (rating: RatingFilter | null) => void
  reset: () => void
}

const CombinedRatingFilterMobile = ({
  id,
  rating,
  title,
  setRating,
  minRange,
  maxRange,
  stepSize,
  reset,
}: Props) => {
  const { t } = useTranslation('strMobileFilter')
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const gameZone = useGameZone()
  const { rangeInputHandlers, internalState, setInternalState, reconcileInternalState } =
    useCombinedRatingFilter({
      rating,
      minRange,
      maxRange,
    })

  const appliedRange = useMemo(() => {
    const { range, type, isGameZoneApplied } = internalState
    const gameZoneValues = gameZone?.[type] && {
      min: (gameZone[type] as GameZoneBounds).upper,
      max: (gameZone[type] as GameZoneBounds).lower,
    }

    return isGameZoneApplied && gameZoneValues ? gameZoneValues : range
  }, [internalState, gameZone])

  useEffect(() => {
    const {
      canApply,
      error,
      inputsRange: { min, max },
      confidence,
      type,
      range,
      isGameZoneApplied,
    } = internalState

    if (canApply && !error && min !== null && max !== null) {
      const gameZoneValues =
        gameZone?.[type] && transformGameZoneValues(gameZone[type] as GameZoneBounds)
      const filter = {
        type,
        confidence,
        range,
        gameZone: isGameZoneApplied && gameZoneValues ? gameZoneValues : undefined,
      }
      setRating(filter)
    } else if (!canApply) {
      setRating(null)
    }
  }, [setRating, internalState, rating, gameZone])

  useEffect(() => {
    if (!filterModalOpen && !!internalState.error) reconcileInternalState()
  }, [filterModalOpen, internalState, reconcileInternalState])

  const makeFilterLabel = (min: number, max: number) =>
    `${transformRatingForLabel(max)}-${transformRatingForLabel(min)}`

  const selectedRangeLabel =
    internalState.canApply && !internalState.error
      ? makeFilterLabel(appliedRange.min, appliedRange.max)
      : t('anyRating')

  return (
    <>
      <FilterMenuSection
        onClick={() => setFilterModalOpen(true)}
        label={title}
        labelSelection={selectedRangeLabel}
      />

      <FilterMenuPage
        controls={{ isOpen: filterModalOpen, setIsOpen: setFilterModalOpen }}
        title={title}
        classNames={{ body: styles.contentContainer }}
        withBackBtn
      >
        <CombinedRatingFilter
          id={id}
          minRange={minRange}
          maxRange={maxRange}
          stepSize={stepSize}
          internalState={internalState}
          setInternalState={setInternalState}
          {...rangeInputHandlers}
          classNames={{
            radio: styles.radio,
            radioGroup: styles.radioGroup,
            radioLabel: styles.radioLabel,
            sliderWrapper: styles.sliderWrapper,
            slider: styles.slider,
            groupLabel: styles.groupLabel,
          }}
          gameZone={gameZone}
        />
        {!internalState.canApply && <Separator className={styles.separator} />}
      </FilterMenuPage>
    </>
  )
}

export default CombinedRatingFilterMobile
